import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { set } from 'lodash';
import omit from 'lodash/omit';
import { useContext, useRef, useState, useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import StatusPill from '../../../../v2/js/components/StatusPill';
import { StatusCodes, WorkflowScopes } from '../../../../v2/js/constants/workflows';
import { useCodeDefinitionsByCategory } from '../../../../v2/js/hooks/useCodeDefinitions';
import { useStaffOrgFilter } from '../../../../v2/js/hooks/useStaffOrgFilter';
import { getActiveOpportunitiesQueryKey } from '../../../../v2/js/pages/Organization/Opportunities/queryKeys';
import {
  ProviderLimitedDetail,
  ProviderDetailSerializerOld,
  HealthcareFacility,
  ApiError,
  ConnectionsService,
  AssignmentsService,
  ShiftRateTypesEnum,
  HealthcareEntityService,
} from '../../../api';
import { FormFeedback } from '../../../app/components/FormFeedback';
import { FormTextareaField } from '../../../app/components/FormTextareaField';
import { SelectController } from '../../../app/components/SelectController';
import { ServerErrorsAlert } from '../../../app/components/ServerErrorsAlert';
import { FormSubmitContext } from '../../../app/contexts/FormSubmitContext';
import { connectionRequestsQueryKeys } from '../../../app/query-keys-factories/connection-requests';
import { healthcareEntityFacilityQueryKeys } from '../../../app/query-keys-factories/facilities';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';

import { handleApiError } from '../../../app/utils/errorhandling';
import { generateShowFieldErrors } from '../../../utils/forms';

export const CreateConnectionDialog = ({
  provider,
  closeDialog,
  isDialogOpen,
  hasConnection = false,
}: {
  provider: ProviderLimitedDetail | ProviderDetailSerializerOld;
  closeDialog: () => void;
  isDialogOpen: boolean;
  hasConnection?: boolean;
}) => {
  const validationSchema = z.object({
    facilities: z.array(z.string()).optional(),
    opportunity: z.string().optional(),
    message: z.string().optional(),
    facilityRates: z
      .record(
        z.string(),
        z.object({
          dayRate: z.number().nullable().optional(),
          nightRate: z.number().nullable().optional(),
          callRate: z.number().nullable().optional(),
          twentyFourHourRate: z.number().nullable().optional(),
        })
      )
      .optional(),
  });

  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const staffOrgFilter = useStaffOrgFilter();
  const formEl = useRef<HTMLFormElement>(null);
  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    message?: boolean;
    facilities?: {
      [key: string]: {
        id: boolean;
      };
    };
    opportunity?: boolean;
    facilityRates?: {
      [facilityId: string]: {
        dayRate?: boolean;
        nightRate?: boolean;
        callRate?: boolean;
        twentyFourHourRate?: boolean;
      };
    };
  }>({});

  const { data: marketplaceData, isLoading: isMarketplaceLoading } = useQuery({
    queryKey: ['marketplace-provider', provider.id],
    queryFn: async () => {
      return HealthcareEntityService.healthcareEntityMarketplaceProviderRetrieve({
        provider: provider.id,
      });
    },
    enabled: isDialogOpen,
  });

  const { data: opportunitiesData, isLoading: isOpportunitiesLoading } = useQuery({
    queryKey: [...getActiveOpportunitiesQueryKey(), { healthcareEntityId: staffOrgFilter }],
    queryFn: async () => {
      return AssignmentsService.assignmentsOpportunitiesList({
        status: [StatusCodes.REOPENED, StatusCodes.NEW, StatusCodes.OPEN],
        healthcareEntityId: staffOrgFilter,
      });
    },
    enabled: isDialogOpen,
  });

  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | null>(null);

  const { data: opportunityDetails } = useQuery({
    queryKey: ['assignments-opportunities', selectedOpportunityId],
    queryFn: async () => {
      if (!selectedOpportunityId) return null;
      return AssignmentsService.assignmentsOpportunitiesRetrieve({
        id: selectedOpportunityId,
        provider: provider.id,
      });
    },
    enabled: !!selectedOpportunityId,
  });

  // Get connected facilities for the provider (excluding rejected/denied connections)
  const { data: connectedFacilitiesData } = useQuery({
    queryKey: ['connected-facilities', provider.id],
    queryFn: async () => {
      return ConnectionsService.connectionsConnectedFacilityList({
        provider: provider.id,
        ordering: 'facility__name',
        statusKind: ['I', 'T'],
      });
    },
    enabled: isDialogOpen,
    select: (data) => data.results,
  });

  const { data: specialties } = useCodeDefinitionsByCategory('Specialty');

  const {
    clearErrors,
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const [facilityOptions, setFacilityOptions] = useState<Array<{ label: string; value: string }>>(
    []
  );

  const [opportunityOptions, setOpportunityOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  // Get all facilities from the selected opportunity, excluding already connected ones
  const getOpportunityFacilities = () => {
    if (!opportunityDetails) return [];

    const facilities: Array<{ id: string; name: string }> = [];

    // Add main facility
    if (opportunityDetails.facility) {
      facilities.push({
        id: opportunityDetails.facility.id,
        name: opportunityDetails.facility.name,
      });
    }

    // Add alternate facilities (these are full facility objects)
    if (
      opportunityDetails.alternate_facilities &&
      Array.isArray(opportunityDetails.alternate_facilities)
    ) {
      facilities.push(
        ...opportunityDetails.alternate_facilities.map((facility: any) => ({
          id: facility.id,
          name: facility.name,
        }))
      );
    }

    // Filter out facilities that are already connected
    if (connectedFacilitiesData && connectedFacilitiesData.length > 0) {
      const connectedFacilityIds = connectedFacilitiesData.map((cf) => cf.facility.id);
      return facilities.filter((facility) => !connectedFacilityIds.includes(facility.id));
    }

    return facilities;
  };

  // Get available rate types for the opportunity
  const getAvailableRateTypes = () => {
    if (!opportunityDetails?.provider_rates) return [];

    const providerRateTypes: ShiftRateTypesEnum[] = [];

    if (opportunityDetails.provider_rates?.facility_rate_day)
      providerRateTypes.push(ShiftRateTypesEnum.DAY);
    if (opportunityDetails.provider_rates?.facility_rate_night)
      providerRateTypes.push(ShiftRateTypesEnum.NIGHT);
    if (opportunityDetails.provider_rates?.facility_rate_call)
      providerRateTypes.push(ShiftRateTypesEnum.CALL);
    if (opportunityDetails.provider_rates?.facility_rate_24h)
      providerRateTypes.push(ShiftRateTypesEnum._24H);
    return providerRateTypes;
  };

  useEffect(() => {
    if (!isMarketplaceLoading && marketplaceData?.facilities) {
      setFacilityOptions(
        marketplaceData.facilities.map((facility) => ({
          label: facility.name,
          value: facility.id,
        }))
      );
    }
  }, [marketplaceData?.facilities, isMarketplaceLoading]);

  useEffect(() => {
    if (!isOpportunitiesLoading && opportunitiesData?.results && specialties) {
      setOpportunityOptions(
        opportunitiesData.results.map((opportunity) => ({
          label: `${opportunity.code}: ${opportunity.facility.name} (${specialties.find((s) => s.code === opportunity.specialty)?.title})`,
          value: opportunity.id,
        }))
      );
    }
  }, [opportunitiesData?.results, isOpportunitiesLoading, specialties]);

  useEffect(() => {
    if (!opportunityDetails || !marketplaceData) return;

    const facilities = getOpportunityFacilities();
    const availableRateTypes = getAvailableRateTypes();
    const newFacilityRates = {};

    facilities.forEach((facility) => {
      availableRateTypes.forEach((rateType) => {
        let providerRate, opportunityRate, fieldName;
        switch (rateType) {
          case ShiftRateTypesEnum.DAY:
            providerRate = opportunityDetails.provider_rates?.facility_rate_day;
            opportunityRate = opportunityDetails.settings?.max_rate_day;
            fieldName = 'dayRate';
            break;
          case ShiftRateTypesEnum.NIGHT:
            providerRate = opportunityDetails.provider_rates?.facility_rate_night;
            opportunityRate = opportunityDetails.settings?.max_rate_night;
            fieldName = 'nightRate';
            break;
          case ShiftRateTypesEnum.CALL:
            providerRate = opportunityDetails.provider_rates?.facility_rate_call;
            opportunityRate = opportunityDetails.settings?.max_rate_call;
            fieldName = 'callRate';
            break;
          case ShiftRateTypesEnum._24H:
            providerRate = opportunityDetails.provider_rates?.facility_rate_24h;
            opportunityRate = opportunityDetails.settings?.max_rate_24h;
            fieldName = 'twentyFourHourRate';
            break;
          default:
            return;
        }
        if (
          providerRate !== null &&
          providerRate !== undefined &&
          (opportunityRate || 0) < providerRate
        ) {
          set(newFacilityRates, [facility.id, fieldName], opportunityRate);
        }
      });
    });
    reset((prev) => ({
      ...prev,
      facilityRates: newFacilityRates,
    }));

    // Only run when opportunityDetails or marketplaceData changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityDetails, marketplaceData]);

  const queryClient = useQueryClient();
  const mutationRequestConnection = useMutation({
    mutationFn: async ({
      facilities,
      opportunity,
      message,
      facility_rate_day,
      facility_rate_night,
      facility_rate_call,
      facility_rate_24h,
      rates_proposed,
    }: {
      facilities: HealthcareFacility['id'][];
      opportunity?: string;
      message?: string;
      facility_rate_day?: number;
      facility_rate_night?: number;
      facility_rate_call?: number;
      facility_rate_24h?: number;
      rates_proposed?: boolean;
    }) => {
      const results = await Promise.allSettled(
        facilities.map(async (facility) =>
          ConnectionsService.connectionsConnectedFacilityCreate({
            requestBody: {
              provider: provider.id,
              facility,
              opportunity,
              message,
              facility_rate_day,
              facility_rate_night,
              facility_rate_call,
              facility_rate_24h,
              rates_proposed,
            },
          })
        )
      );

      const failures = results.filter(
        (result) => result.status === 'rejected'
      ) as PromiseRejectedResult[];

      if (failures.length > 0) {
        throw failures[0].reason;
      }

      return results;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists());
      await queryClient.invalidateQueries(providerQueryKeys.allDetails());
      await queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists());
      toast.success('Connection request sent successfully');
      handleHide();
    },
    onError: (error: ApiError) => {
      if (error.status === 400) {
        const fieldErrors = omit(error.body, ['non_field_errors']);
        setServerErrors(error);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setShowGlobalServerErrors(true);
      }
    },
  });

  const onSubmit = async (data) => {
    try {
      const opportunityFacilities = getOpportunityFacilities();

      const connectionRequests = opportunityFacilities.map((facility) => {
        const facilityRates = data.facilityRates?.[facility.id] || {};

        const rates = {
          ...(facilityRates.dayRate && { facility_rate_day: facilityRates.dayRate }),
          ...(facilityRates.nightRate && { facility_rate_night: facilityRates.nightRate }),
          ...(facilityRates.callRate && { facility_rate_call: facilityRates.callRate }),
          ...(facilityRates.twentyFourHourRate && {
            facility_rate_24h: facilityRates.twentyFourHourRate,
          }),
        };

        // Only include rates_proposed if there are actual rates
        const hasRates = Object.keys(rates).length > 0;

        return {
          facility: facility.id,
          opportunity: data.opportunity,
          message: data.message,
          ...rates,
          ...(hasRates && { rates_proposed: true }),
        };
      });

      // Send connection requests for all facilities
      const results = await Promise.allSettled(
        connectionRequests.map(async (requestData) =>
          ConnectionsService.connectionsConnectedFacilityCreate({
            requestBody: {
              provider: provider.id,
              ...requestData,
            },
          })
        )
      );

      const failures = results.filter(
        (result) => result.status === 'rejected'
      ) as PromiseRejectedResult[];

      if (failures.length > 0) {
        throw failures[0].reason;
      }

      // Invalidate queries and show success message
      await queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists());
      await queryClient.invalidateQueries(providerQueryKeys.allDetails());
      await queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists());
      toast.success('Connection request sent successfully');
      handleHide();
    } catch (error) {
      handleApiError(error, { showToast: false });
      if (error.status === 400) {
        const fieldErrors = omit(error.body, ['non_field_errors']);
        setServerErrors(error);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setShowGlobalServerErrors(true);
      }
    }
  };

  const handleHide = () => {
    reset({
      facilities: [],
      opportunity: '',
      message: '',
      facilityRates: {},
    });
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
    setSelectedOpportunityId(null);
    closeDialog();
  };

  function handleClearErrorsAlert() {
    setShowGlobalServerErrors(false);
  }

  const handleSubmitClick = () => {
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
    formEl?.current?.requestSubmit();
  };

  // const [showRateProposal, setShowRateProposal] = useState(false);

  const allOpportunityFacilities = (() => {
    if (!opportunityDetails) return [];
    const facilities: string[] = [];
    if (opportunityDetails.facility) {
      facilities.push(opportunityDetails.facility.id);
    }
    if (
      opportunityDetails.alternate_facilities &&
      Array.isArray(opportunityDetails.alternate_facilities)
    ) {
      facilities.push(...opportunityDetails.alternate_facilities.map((f: any) => f.id));
    }
    return facilities;
  })();

  // Filter connected facilities to only show those that are part of the selected opportunity
  const opportunityConnectedFacilities =
    connectedFacilitiesData?.filter((cf) => allOpportunityFacilities.includes(cf.facility.id)) ||
    [];

  const connectedFacilityIds = connectedFacilitiesData?.map((cf) => cf.facility.id) || [];
  const allConnected =
    allOpportunityFacilities.length > 0 &&
    allOpportunityFacilities.every((id) => connectedFacilityIds.includes(id));

  return (
    <Modal show={isDialogOpen} onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>{hasConnection ? 'Add a Facility' : 'Request Connection'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ServerErrorsAlert
          errors={serverErrors}
          className="m-3"
          showErrors={showGlobalServerErrors}
          clearErrors={handleClearErrorsAlert}
        />
        <Form ref={formEl} noValidate onSubmit={handleSubmit(onSubmit)}>
          <Form.Group className="mb-3" controlId="opportunity">
            <SelectController
              key={opportunityOptions.length}
              control={control}
              name="opportunity"
              placeholder={
                isOpportunitiesLoading ? 'Loading opportunities...' : 'Select an opportunity...'
              }
              isMulti={false}
              options={opportunityOptions}
              isClearable={true}
              onChange={(value: any) => {
                setShowServerFieldErrors(omit(showServerFieldErrors, 'opportunity'));
                setSelectedOpportunityId(value?.value || null);
              }}
            />
            <FormFeedback
              clientError={errors.opportunity}
              serverErrors={
                showServerFieldErrors.opportunity ? serverErrors?.body?.opportunity : null
              }
            />
          </Form.Group>

          <div className="mb-3">
            {opportunityDetails?.provider_rates && (
              <div>
                {getOpportunityFacilities().length > 0 && (
                  <div className="tw:mt-2 tw:p-2 tw:text-brand-green-0 tw:bg-brand-green-1/10 tw:border tw:border-brand-green-1/20 tw:rounded-md tw:text-sm">
                    <i className="far fa-info-circle tw:text-brand-green-0 tw:mt-0.5 tw:mr-1" />
                    Rates shown may differ from the rates listed on the Provider Profile or other
                    Opportunities. The rates shown below are based on the specific Specialty set on
                    the Opportunity you have selected. The Provider&apos;s Marketplace Rate is based
                    on their listed specialties.
                    <br />
                    <br />
                    If you want to propose different rates than requested, you can enter an HCO
                    Offer amount, otherwise you can leave it blank if you agree to the all-in rate.
                  </div>
                )}
                {getOpportunityFacilities().length > 0 && (
                  <div className="mt-2 border rounded p-3 pb-0">
                    {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
                    {getOpportunityFacilities().map((facility, index) => {
                      const availableRateTypes = getAvailableRateTypes();
                      const isLast = index === getOpportunityFacilities().length - 1;

                      return (
                        <div key={facility.id} className={isLast ? '' : 'mb-4'}>
                          <h6 className="mb-3 fw-bold border-bottom pb-2">{facility.name}</h6>
                          <div className="row mb-2 fst-italic text-muted">
                            <div className="col-4">Type</div>
                            <div className="col-4">All-in Rate</div>
                            <div className="col-4">HCO Offer</div>
                          </div>

                          {availableRateTypes.includes(ShiftRateTypesEnum.DAY) && (
                            <div className="row align-items-center mb-3">
                              <div className="col-4">Day</div>
                              <div className="col-4">
                                ${opportunityDetails.provider_rates?.facility_rate_day}/hr
                              </div>
                              <div className="col-4">
                                <Form.Control
                                  type="number"
                                  min="1"
                                  defaultValue=""
                                  {...register(`facilityRates.${facility.id}.dayRate`, {
                                    setValueAs: (v) => (v === '' ? null : Number(v)),
                                  })}
                                  className="w-100 no-number-spinner tw:selection:bg-brand-green-2"
                                />
                                <FormFeedback
                                  clientError={errors.facilityRates?.[facility.id]?.dayRate}
                                  serverErrors={
                                    showServerFieldErrors.facilityRates?.[facility.id]?.dayRate
                                      ? serverErrors?.body?.facilityRates?.[facility.id]?.dayRate
                                      : null
                                  }
                                />
                              </div>
                            </div>
                          )}

                          {availableRateTypes.includes(ShiftRateTypesEnum.NIGHT) && (
                            <div className="row align-items-center mb-3">
                              <div className="col-4">Night</div>
                              <div className="col-4">
                                ${opportunityDetails.provider_rates?.facility_rate_night}/hr
                              </div>
                              <div className="col-4">
                                <Form.Control
                                  type="number"
                                  min="1"
                                  defaultValue=""
                                  {...register(`facilityRates.${facility.id}.nightRate`, {
                                    setValueAs: (v) => (v === '' ? null : Number(v)),
                                  })}
                                  className="w-100 no-number-spinner tw:selection:bg-brand-green-2"
                                />
                                <FormFeedback
                                  clientError={errors.facilityRates?.[facility.id]?.nightRate}
                                  serverErrors={
                                    showServerFieldErrors.facilityRates?.[facility.id]?.nightRate
                                      ? serverErrors?.body?.facilityRates?.[facility.id]?.nightRate
                                      : null
                                  }
                                />
                              </div>
                            </div>
                          )}

                          {availableRateTypes.includes(ShiftRateTypesEnum.CALL) && (
                            <div className="row align-items-center mb-3">
                              <div className="col-4">Call</div>
                              <div className="col-4">
                                ${opportunityDetails.provider_rates?.facility_rate_call}/hr
                              </div>
                              <div className="col-4">
                                <Form.Control
                                  type="number"
                                  min="1"
                                  defaultValue=""
                                  {...register(`facilityRates.${facility.id}.callRate`, {
                                    setValueAs: (v) => (v === '' ? null : Number(v)),
                                  })}
                                  className="w-100 no-number-spinner tw:selection:bg-brand-green-2"
                                />
                                <FormFeedback
                                  clientError={errors.facilityRates?.[facility.id]?.callRate}
                                  serverErrors={
                                    showServerFieldErrors.facilityRates?.[facility.id]?.callRate
                                      ? serverErrors?.body?.facilityRates?.[facility.id]?.callRate
                                      : null
                                  }
                                />
                              </div>
                            </div>
                          )}

                          {availableRateTypes.includes(ShiftRateTypesEnum._24H) && (
                            <div className="row align-items-center mb-3">
                              <div className="col-4">24-Hour</div>
                              <div className="col-4">
                                ${opportunityDetails.provider_rates?.facility_rate_24h}/hr
                              </div>
                              <div className="col-4">
                                <Form.Control
                                  type="number"
                                  min="1"
                                  defaultValue=""
                                  {...register(`facilityRates.${facility.id}.twentyFourHourRate`, {
                                    setValueAs: (v) => (v === '' ? null : Number(v)),
                                  })}
                                  className="w-100 no-number-spinner tw:selection:bg-brand-green-2"
                                />
                                <FormFeedback
                                  clientError={
                                    errors.facilityRates?.[facility.id]?.twentyFourHourRate
                                  }
                                  serverErrors={
                                    showServerFieldErrors.facilityRates?.[facility.id]
                                      ?.twentyFourHourRate
                                      ? serverErrors?.body?.facilityRates?.[facility.id]
                                          ?.twentyFourHourRate
                                      : null
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Connected Facilities Section */}
                {opportunityConnectedFacilities.length > 0 && (
                  <div className="mb-3">
                    <div className="tw:my-2 tw:p-2 tw:text-warning-text tw:bg-warning-bg/50 tw:border tw:border-amber-500/20 tw:rounded-md tw:text-sm">
                      <i className="far fa-warning tw:text-warning-text tw:mt-0.5 tw:mr-1" />
                      {allConnected
                        ? 'All facilities in this opportunity are already connected to this provider.'
                        : 'Your organization is already connected with this provider at these locations:'}
                    </div>
                    <div className="border rounded p-3">
                      {opportunityConnectedFacilities.map((connectedFacility, index) => (
                        <div
                          key={connectedFacility.id}
                          className={`mb-3 pb-3 ${index < opportunityConnectedFacilities.length - 1 ? 'border-bottom' : ''}`}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="mb-1">{connectedFacility.facility.name}</h6>

                            {connectedFacility.current_status && (
                              <StatusPill
                                scope={WorkflowScopes.ConnectedFacility}
                                status={connectedFacility.current_status!}
                              />
                            )}
                          </div>

                          {/* Display rates if they exist */}
                          {(connectedFacility.facility_rate_day ||
                            connectedFacility.facility_rate_night ||
                            connectedFacility.facility_rate_call ||
                            connectedFacility.facility_rate_24h) && (
                            <div className="row text-muted small">
                              {(connectedFacility.facility_rate_day || 0) > 0 && (
                                <div className="col-3">
                                  <strong>Day:</strong> ${connectedFacility.facility_rate_day}/hr
                                </div>
                              )}
                              {(connectedFacility.facility_rate_night || 0) > 0 && (
                                <div className="col-3">
                                  <strong>Night:</strong> ${connectedFacility.facility_rate_night}
                                  /hr
                                </div>
                              )}
                              {(connectedFacility.facility_rate_call || 0) > 0 && (
                                <div className="col-3">
                                  <strong>Call:</strong> ${connectedFacility.facility_rate_call}/hr
                                </div>
                              )}
                              {(connectedFacility.facility_rate_24h || 0) > 0 && (
                                <div className="col-3">
                                  <strong>24H:</strong> ${connectedFacility.facility_rate_24h}
                                  /hr
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {!marketplaceData && (
              <div className="mt-2 text-center text-muted">Loading rates...</div>
            )}
          </div>

          <FormTextareaField
            label="Message"
            name="message"
            placeholder="Enter a message to send to the provider"
            register={register}
            rows={5}
            errors={errors}
            serverErrors={serverErrors?.body?.message}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={handleHide} tabIndex={-1}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          onClick={handleSubmitClick}
          disabled={
            mutationRequestConnection.isLoading || !isSubmitEnabled || !facilityOptions.length
          }
        >
          {mutationRequestConnection.isLoading ? 'Sending...' : 'Send request'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
